// React imports
import React, { useState, type JSX } from "react";

// Redux imports
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";
import { sendMessage } from "./ChatSlice_Store";
import { selectCurrentProjectId } from "./ProjectSlice_Store";
import ChatComposerForm from "./components/chat/ChatComposerForm";
import { SelectFeatureHint, ChatInputFooter } from "./components/chat/ChatInputChrome";

interface ChatInputProps {
  ariaLabel?: string;
  value?: string;
  onChange?: (value: string) => void;
}

/** Blur the focused element so the on-screen keyboard retracts after sending. */
function dismissVirtualKeyboard(): void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return;
  }
  if (!window.matchMedia("(pointer: coarse)").matches) {
    return;
  }
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement) {
    activeElement.blur();
  }
}

// Default export of ChatInput_Component
export default function ChatInput_Component({
  ariaLabel = "Message input field",
  value,
  onChange,
}: ChatInputProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [internalInputText, setInternalInputText] = useState<string>("");

  // Use controlled value if provided, otherwise use internal state
  const inputText = value !== undefined ? value : internalInputText;
  const setInputText = (next: string): void =>
    onChange ? onChange(next) : setInternalInputText(next);

  const projectReady = Boolean(useAppSelector(selectCurrentProjectId));
  const chatInputReady = useAppSelector((state) => state.chatSlice.chatInputReady);
  const useCaseSelected = useAppSelector((state) => state.chatSlice.useCaseSelected);

  // Allow typing but prevent sending until project is ready, input is unlocked, and use case is selected
  const sendDisabled =
    inputText.trim() === "" || !projectReady || !chatInputReady || !useCaseSelected;

  const send = (): void => {
    if (sendDisabled) return;
    void dispatch(sendMessage(inputText.trim()));
    setInputText("");
    // Dismiss the virtual keyboard on touch devices after sending
    dismissVirtualKeyboard();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    // Enter without Shift submits the message; Shift+Enter allows a new line
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="relative w-full bg-transparent border-t border-cvc-grey p-3 sm:p-4">
      {!useCaseSelected && <SelectFeatureHint />}

      <ChatComposerForm
        ariaLabel={ariaLabel}
        inputText={inputText}
        projectReady={projectReady}
        chatInputReady={chatInputReady}
        useCaseSelected={useCaseSelected}
        sendDisabled={sendDisabled}
        onInputChange={setInputText}
        onKeyDown={handleKeyDown}
        onSubmit={send}
      />

      <ChatInputFooter />
    </div>
  );
}
